import React, { useCallback, useEffect, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table'
import { IMaskInput } from 'react-imask'
import IMask from 'imask'
import * as moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import {
  TrainingSessionInterval,
  EditableInterval,
} from '../TrainingSessionsTable/utils'

momentDurationFormatSetup(moment)

// ─── Distance presets ────────────────────────────────────────────────────────

const KM_OPTIONS = Array.from({ length: 42 }, (_, i) => {
  const meters = (i + 1) * 1000
  return {
    label: `${i + 1} km (${meters.toLocaleString()}m)`,
    meters,
    unit: 'km' as const,
  }
})

const MILE_OPTIONS = Array.from({ length: 26 }, (_, i) => {
  const meters = Math.round((i + 1) * 1609.344)
  return {
    label: `${i + 1} mile${i > 0 ? 's' : ''} (${meters.toLocaleString()}m)`,
    meters,
    unit: 'mi' as const,
  }
})

function getDistanceOptions(unitSystem?: string | null) {
  if (unitSystem === 'imperial') return MILE_OPTIONS
  if (unitSystem === 'metric') return KM_OPTIONS
  return [...KM_OPTIONS, ...MILE_OPTIONS].sort((a, b) => a.meters - b.meters)
}

function findDistanceOption(
  meters: number | null | undefined,
  unitSystem?: string | null
) {
  if (!meters) return ''
  const opts = getDistanceOptions(unitSystem)
  const match = opts.find((o) => o.meters === meters)
  return match ? String(match.meters) : ''
}

function getDistanceLabel(
  meters: number | null | undefined,
  unitSystem?: string | null
) {
  if (!meters) return 'N/A'
  const opts = getDistanceOptions(unitSystem)
  const match = opts.find((o) => o.meters === meters)
  if (match) return match.label
  return `${meters.toLocaleString()} m`
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const msToHHMMSS = (ms: number): string => {
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    s
  ).padStart(2, '0')}`
}

const hhmmssToMs = (val: string): number | null => {
  const match = val.match(/^(\d{1,3}):(\d{2}):(\d{2})$/)
  if (!match) return null
  return (
    (parseInt(match[1], 10) * 3600 +
      parseInt(match[2], 10) * 60 +
      parseInt(match[3], 10)) *
    1000
  )
}

const msToMinSec = (ms: number): string => {
  const totalSec = Math.floor(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const minSecToMs = (val: string): number | null => {
  const match = val.match(/^(\d{1,3}):(\d{2})$/)
  if (!match) return null
  return (parseInt(match[1], 10) * 60 + parseInt(match[2], 10)) * 1000
}

interface IProps {
  intervals: TrainingSessionInterval[]
  isEditMode?: boolean
  sessionTempId?: string
  editedIntervals?: EditableInterval[]
  unitSystem?: string | null
  onIntervalChange?: (
    sessionTempId: string,
    intervalTempId: string,
    field: string,
    value: unknown
  ) => void
  onIntervalBlur?: () => void
  onAddInterval?: (sessionTempId: string) => void
  onDeleteInterval?: (sessionTempId: string, intervalTempId: string) => void
  onDuplicateInterval?: (sessionTempId: string, intervalTempId: string) => void
  onReorderIntervals?: (
    sessionTempId: string,
    fromIndex: number,
    toIndex: number
  ) => void
}

function IntervalEditRow({
  interval,
  index,
  sessionTempId,
  unitSystem,
  onIntervalChange,
  onIntervalBlur,
  onDelete,
  onDuplicate,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver,
}: {
  interval: EditableInterval
  index: number
  sessionTempId: string
  unitSystem?: string | null
  onIntervalChange?: IProps['onIntervalChange']
  onIntervalBlur?: IProps['onIntervalBlur']
  onDelete: () => void
  onDuplicate: () => void
  onDragStart: (e: React.DragEvent, idx: number) => void
  onDragOver: (e: React.DragEvent, idx: number) => void
  onDrop: (e: React.DragEvent, idx: number) => void
  isDragOver: boolean
}) {
  const [goalTimeInput, setGoalTimeInput] = useState(
    interval.goalTimeMs ? msToHHMMSS(interval.goalTimeMs) : ''
  )
  const [goalPaceInput, setGoalPaceInput] = useState(
    interval.goalPaceMsPerKm ? msToMinSec(interval.goalPaceMsPerKm) : ''
  )

  useEffect(() => {
    setGoalTimeInput(interval.goalTimeMs ? msToHHMMSS(interval.goalTimeMs) : '')
  }, [interval.goalTimeMs])

  useEffect(() => {
    setGoalPaceInput(
      interval.goalPaceMsPerKm ? msToMinSec(interval.goalPaceMsPerKm) : ''
    )
  }, [interval.goalPaceMsPerKm])

  const inputCls =
    'border border-blue-300 rounded px-2 py-1 text-xs bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400'

  const hasDistanceSelection =
    interval.goalDistanceMeters != null &&
    Number(interval.goalDistanceMeters) > 0
  const hasGoalTime =
    interval.goalTimeMs != null && Number(interval.goalTimeMs) > 0
  const distanceTimeConflict = hasDistanceSelection && hasGoalTime
  const distanceTimeMissing = !hasDistanceSelection && !hasGoalTime
  const showDistanceTimeError = distanceTimeConflict || distanceTimeMissing

  const hasGoalPace =
    interval.goalPaceMsPerKm != null && Number(interval.goalPaceMsPerKm) > 0
  const hasPaceExpression = !!interval.goalPaceExpression?.trim()
  const showPaceError = !hasGoalPace && !hasPaceExpression

  return (
    <tr
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      className={`border-t border-gray-200 ${
        isDragOver ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''
      }`}
    >
      <td className="px-2 py-1 cursor-grab text-gray-400 select-none">⠿</td>
      <td className="px-2 py-1 text-center text-xs text-gray-500 w-8">
        {interval.intervalNo}
      </td>
      <td className="px-2 py-1">
        <input
          type="text"
          value={interval.title}
          onChange={(e) =>
            onIntervalChange?.(
              sessionTempId,
              interval.tempId,
              'title',
              e.target.value
            )
          }
          onBlur={onIntervalBlur}
          className={`w-36 ${inputCls}`}
        />
      </td>
      <td className="px-2 py-1">
        <IMaskInput
          mask="HH:MM:SS"
          blocks={{
            HH: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2 },
            MM: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 },
            SS: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 },
          }}
          value={goalTimeInput}
          onAccept={(val: string) => setGoalTimeInput(val)}
          onBlur={() => {
            if (!goalTimeInput.trim()) {
              onIntervalChange?.(
                sessionTempId,
                interval.tempId,
                'goalTimeMs',
                null
              )
            } else {
              const ms = hhmmssToMs(goalTimeInput)
              if (ms !== null)
                onIntervalChange?.(
                  sessionTempId,
                  interval.tempId,
                  'goalTimeMs',
                  ms
                )
            }
            onIntervalBlur?.()
          }}
          placeholder="hh:mm:ss"
          className={`w-24 ${inputCls} ${
            showDistanceTimeError ? 'border-red-400' : ''
          }`}
        />
        {showDistanceTimeError && (
          <div className="mt-0.5 text-[10px] text-red-500">
            Pick exactly one: time or distance
          </div>
        )}
      </td>
      <td className="px-2 py-1">
        <div className="flex items-center gap-1">
          <select
            value={findDistanceOption(interval.goalDistanceMeters, unitSystem)}
            onChange={(e) => {
              const meters =
                e.target.value === '' ? null : Number(e.target.value)
              onIntervalChange?.(
                sessionTempId,
                interval.tempId,
                'goalDistanceMeters',
                meters
              )
              onIntervalBlur?.()
            }}
            className={`w-32 ${inputCls} ${
              showDistanceTimeError ? 'border-red-400' : ''
            }`}
          >
            <option value="">— select —</option>
            {getDistanceOptions(unitSystem).map((opt) => (
              <option key={`${opt.unit}-${opt.meters}`} value={opt.meters}>
                {opt.label}
              </option>
            ))}
          </select>
          {interval.goalDistanceMeters != null &&
            Number(interval.goalDistanceMeters) > 0 && (
              <button
                type="button"
                onClick={() => {
                  onIntervalChange?.(
                    sessionTempId,
                    interval.tempId,
                    'goalDistanceMeters',
                    null
                  )
                  onIntervalBlur?.()
                }}
                className="px-1 text-red-500 hover:text-red-700 text-sm leading-none"
                title="Clear distance selection"
                aria-label="Clear distance selection"
              >
                ×
              </button>
            )}
        </div>
        {showDistanceTimeError && (
          <div className="mt-0.5 text-[10px] text-red-500">
            Pick exactly one: time or distance
          </div>
        )}
      </td>
      <td className="px-2 py-1">
        <input
          type="text"
          readOnly
          value={
            interval.goalDistanceMeters != null
              ? `${interval.goalDistanceMeters.toLocaleString()} m`
              : ''
          }
          placeholder="—"
          className="w-24 border border-gray-200 rounded px-2 py-1 text-xs bg-gray-50 text-gray-500 cursor-not-allowed"
        />
      </td>
      <td className="px-2 py-1">
        <IMaskInput
          mask="MM:SS"
          blocks={{
            MM: { mask: IMask.MaskedRange, from: 0, to: 99, maxLength: 2 },
            SS: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 },
          }}
          value={goalPaceInput}
          onAccept={(val: string) => setGoalPaceInput(val)}
          onBlur={() => {
            if (!goalPaceInput.trim()) {
              onIntervalChange?.(
                sessionTempId,
                interval.tempId,
                'goalPaceMsPerKm',
                null
              )
            } else {
              const ms = minSecToMs(goalPaceInput)
              if (ms !== null)
                onIntervalChange?.(
                  sessionTempId,
                  interval.tempId,
                  'goalPaceMsPerKm',
                  ms
                )
            }
            onIntervalBlur?.()
          }}
          placeholder="mm:ss"
          className={`w-20 ${inputCls} ${
            showPaceError ? 'border-red-400' : ''
          }`}
        />
        {showPaceError && (
          <div className="mt-0.5 text-[10px] text-red-500">
            Goal pace (or pace expression) is required
          </div>
        )}
      </td>
      <td className="px-2 py-1">
        <input
          type="text"
          readOnly
          value={interval.goalPaceExpression ?? ''}
          className="w-24 border border-gray-200 rounded px-2 py-1 text-xs bg-gray-50 text-gray-500 cursor-not-allowed"
        />
      </td>
      <td className="px-2 py-1">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onDuplicate}
            title="Duplicate"
            className="text-blue-500 hover:text-blue-700 text-base leading-none"
          >
            ⧉
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Delete"
            className="text-red-500 hover:text-red-700 text-base leading-none"
          >
            🗑
          </button>
        </div>
      </td>
    </tr>
  )
}

const TrainingSessionIntervals = ({
  intervals,
  isEditMode = false,
  sessionTempId,
  editedIntervals,
  unitSystem,
  onIntervalChange,
  onIntervalBlur,
  onAddInterval,
  onDeleteInterval,
  onDuplicateInterval,
  onReorderIntervals,
}: IProps) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    setDragIndex(idx)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault()
    setDragOverIndex(idx)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, toIdx: number) => {
      e.preventDefault()
      if (dragIndex !== null && dragIndex !== toIdx && sessionTempId) {
        onReorderIntervals?.(sessionTempId, dragIndex, toIdx)
      }
      setDragIndex(null)
      setDragOverIndex(null)
    },
    [dragIndex, sessionTempId, onReorderIntervals]
  )

  if (isEditMode && sessionTempId) {
    const rows = editedIntervals ?? []
    return (
      <div className="pl-10 pr-4 py-3 bg-gray-50 border-t border-gray-200">
        <table className="w-full text-sm border border-gray-200 rounded">
          <thead>
            <tr className="bg-gray-100 text-xs text-gray-600">
              <th className="px-2 py-1 w-6" aria-label="Drag" />
              <th className="px-2 py-1 text-center w-8">No</th>
              <th className="px-2 py-1 text-left">Title</th>
              <th className="px-2 py-1 text-left">Goal time (hh:mm:ss)</th>
              <th className="px-2 py-1 text-left">Distance selection</th>
              <th className="px-2 py-1 text-left">Goal distance (metres)</th>
              <th className="px-2 py-1 text-left">Goal pace (min/km)</th>
              <th className="px-2 py-1 text-left">Pace Expression</th>
              <th className="px-2 py-1 w-16" aria-label="Actions" />
            </tr>
          </thead>
          <tbody onDragLeave={() => setDragOverIndex(null)}>
            {rows.map((interval, idx) => (
              <IntervalEditRow
                key={interval.tempId}
                interval={interval}
                index={idx}
                sessionTempId={sessionTempId}
                unitSystem={unitSystem}
                onIntervalChange={onIntervalChange}
                onIntervalBlur={onIntervalBlur}
                onDelete={() =>
                  onDeleteInterval?.(sessionTempId, interval.tempId)
                }
                onDuplicate={() =>
                  onDuplicateInterval?.(sessionTempId, interval.tempId)
                }
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isDragOver={dragOverIndex === idx}
              />
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() => onAddInterval?.(sessionTempId)}
          className="mt-2 px-3 py-1 text-xs rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
        >
          + Add split
        </button>
      </div>
    )
  }

  // View mode — existing react-table
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const columns = React.useMemo<ColumnDef<TrainingSessionInterval>[]>(
    () => [
      {
        accessorKey: 'id',
        enableColumnFilter: true,
        header: () => 'ID',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'title',
        header: () => 'Title',
        footer: (props) => props.column.id,
      },
      {
        id: 'intervalNo',
        accessorKey: 'intervalNo',
        header: () => 'Interval No',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'goalTimeMs',
        header: () => 'Goal Time',
        cell: ({ row }) =>
          row.original?.goalTimeMs
            ? moment.duration(row.original?.goalTimeMs)?.format('mm:ss')
            : 'N/A',
        footer: (props) => props.column.id,
      },
      {
        id: 'distanceSelection',
        header: () => 'Distance selection',
        cell: ({ row }) =>
          getDistanceLabel(row.original?.goalDistanceMeters, unitSystem),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'goalDistanceMeters',
        header: () => 'Goal Distance',
        cell: ({ row }) =>
          row.original?.goalDistanceMeters
            ? row.original?.goalDistanceMeters?.toLocaleString()
            : 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'goalPaceMsPerKm',
        header: () => 'Goal Pace',
        cell: ({ row }) =>
          row.original?.goalPaceMsPerKm
            ? moment.duration(row.original?.goalPaceMsPerKm)?.format('mm:ss')
            : 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'goalPaceExpression',
        header: () => 'Goal Pace Expression',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'recordedTimeMs',
        header: () => 'Recorded Time (ms)',
        cell: ({ row }) =>
          row.original?.recordedTimeMs
            ? moment.duration(row.original?.recordedTimeMs)?.format('mm:ss')
            : 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'recordedDistanceMeters',
        header: () => 'Recorded Distance',
        cell: ({ row }) =>
          row.original?.recordedDistanceMeters
            ? row.original?.recordedDistanceMeters?.toLocaleString()
            : 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'recordedPaceMsPerKm',
        header: () => 'Recorded Pace',
        cell: ({ row }) =>
          row.original?.recordedPaceMsPerKm
            ? moment
                .duration(row.original?.recordedPaceMsPerKm)
                ?.format('mm:ss')
            : 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'level',
        header: () => 'Level',
        cell: ({ row }) => row.original?.level || 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'reps',
        header: () => 'Reps',
        cell: ({ row }) => row.original?.reps || 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'url',
        header: () => 'Url',
        cell: ({ row }) => row.original?.url || 'N/A',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'createdAt',
        header: () => 'Created At',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'updatedAt',
        header: () => 'Updated At',
        footer: (props) => props.column.id,
      },
    ],
    [unitSystem]
  )

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const table = useReactTable({
    data: intervals,
    columns,
    state: {
      pagination: { pageSize: 500, pageIndex: 0 },
    },
    initialState: {
      sorting: [{ id: 'intervalNo', desc: false }],
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <table className="min-w-full">
      <thead className="bg-white">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                className="text-center"
              >
                {header.isPlaceholder ? null : (
                  <div>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </div>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="bg-white">
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="border-t border-gray-200">
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 border-x border-gray-200 text-center"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default TrainingSessionIntervals
